import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "../../components/PortalLayout";
import { api, formatCurrencyEUR, formatDate, formatNumber } from "../../lib/api";

const PAGE_SIZE = 25;

export default function AdminDashboardPage() {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [aum, setAum] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([api.get("/admin/users"), api.get("/admin/invites")])
      .then(([u, i]) => {
        setUsers(u.data);
        setInvites(i.data);
      })
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, []);

  const fetchAum = useCallback(async (q, offset, append) => {
    const { data } = await api.get("/admin/aum/breakdown", {
      params: { search: q || undefined, limit: PAGE_SIZE, offset },
    });
    setAum(data);
    setRows((prev) => (append ? [...prev, ...data.rows] : data.rows));
    return data;
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchAum(search, 0, false).catch((e) => setError(e.response?.data?.detail || e.message));
    }, 300);
    return () => clearTimeout(t);
  }, [search, fetchAum]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      await fetchAum(search, rows.length, true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const stats = useMemo(() => {
    const clients = users.filter((u) => u.role === "client");
    const activeInvites = invites.filter((i) => !i.used_at && new Date(i.expires_at) > new Date());
    return { clients: clients.length, activeInvites: activeInvites.length };
  }, [users, invites]);

  const totals = aum?.totals;
  const hasMore = aum ? rows.length < aum.count : false;

  return (
    <PortalLayout>
      <section className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8" data-testid="admin-dashboard-page">
        <div className="br-fade-up">
          <div className="br-overline">Back office</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3">
            Cruscotto <em>operativo</em>.
          </h1>
          <p className="text-[1.05rem] text-[color:var(--br-text)] mt-3 max-w-[640px]">
            Vista d&apos;insieme sui membri, gli inviti attivi e gli AUM.
          </p>
        </div>

        {error && <div className="mt-6 br-card p-4 text-[color:var(--br-red)]" data-testid="admin-error">{String(error)}</div>}

        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 br-fade-up br-delay-1" data-testid="admin-kpis">
          <KPI label="AUM totale" value={totals ? formatCurrencyEUR(totals.aum) : "…"} accent testid="kpi-aum" />
          <KPI label="Maturato netto" value={totals ? `+ ${formatCurrencyEUR(totals.accrued_net)}` : "…"} testid="kpi-accrued-total" />
          <KPI label="Clienti attivi" value={String(stats.clients)} testid="kpi-clients" />
          <KPI label="Inviti aperti" value={String(stats.activeInvites)} testid="kpi-invites" />
        </div>

        {/* AUM per client breakdown */}
        <div className="mt-14 br-fade-up br-delay-2" data-testid="aum-breakdown-section">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <h2 className="font-serif-display text-[2rem]">AUM per <em>cliente</em></h2>
              {totals && (
                <p className="text-[0.85rem] text-[color:var(--br-text-dim)] mt-2 font-mono-fin" data-testid="aum-flow-summary">
                  Flusso: {formatCurrencyEUR(totals.daily_net)}/giorno · {formatCurrencyEUR(totals.monthly_est_net)}/mese · {formatCurrencyEUR(totals.yearly_est_net)}/anno
                </p>
              )}
            </div>
            <input
              className="br-input"
              style={{ maxWidth: 320 }}
              placeholder="Cerca cliente per nome o email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="aum-search-input"
            />
          </div>

          <div className="br-card overflow-x-auto" data-testid="aum-breakdown-table">
            <table className="w-full text-left">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                  <Th>Cliente</Th>
                  <Th align="right">Capitale</Th>
                  <Th align="right">% AUM</Th>
                  <Th align="right">Maturato netto</Th>
                  <Th align="right">€/giorno</Th>
                  <Th align="right">Stima mese</Th>
                  <Th align="right">Depositi</Th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-8 text-center text-[color:var(--br-text-dim)]" data-testid="aum-empty">
                      {aum ? "Nessun cliente con capitale trovato." : "Caricamento…"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.user_id} className="border-b" style={{ borderColor: "var(--br-line)" }} data-testid={`aum-row-${r.user_id}`}>
                      <Td>
                        <div className="text-[color:var(--br-parchment)]">{r.name}</div>
                        <div className="text-[0.72rem] font-mono-fin text-[color:var(--br-text-dim)]">{r.email}</div>
                      </Td>
                      <Td align="right" className="font-mono-fin text-[color:var(--br-parchment)]" data-testid={`aum-principal-${r.user_id}`}>
                        {formatCurrencyEUR(r.principal)}
                      </Td>
                      <Td align="right" className="font-mono-fin text-[color:var(--br-text-dim)]">{formatNumber(r.share_pct, 1)}%</Td>
                      <Td align="right" className="font-mono-fin" style={{ color: "var(--br-bronze-soft)" }} data-testid={`aum-accrued-${r.user_id}`}>
                        + {formatCurrencyEUR(r.accrued_net)}
                      </Td>
                      <Td align="right" className="font-mono-fin" data-testid={`aum-daily-${r.user_id}`}>{formatCurrencyEUR(r.daily_net)}</Td>
                      <Td align="right" className="font-mono-fin" style={{ color: "var(--br-bronze)" }}>{formatCurrencyEUR(r.monthly_est_net)}</Td>
                      <Td align="right" className="font-mono-fin text-[color:var(--br-text-dim)]">
                        {r.active_deposits}/{r.deposits}
                      </Td>
                    </tr>
                  ))
                )}
              </tbody>
              {totals && rows.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: "1px solid var(--br-line-bright)" }} data-testid="aum-totals-row">
                    <Td className="font-mono-fin text-[0.7rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)]">
                      Totale · {totals.clients_with_capital} clienti
                    </Td>
                    <Td align="right" className="font-mono-fin text-[color:var(--br-parchment)]">{formatCurrencyEUR(totals.aum)}</Td>
                    <Td align="right" className="font-mono-fin text-[color:var(--br-text-dim)]">100%</Td>
                    <Td align="right" className="font-mono-fin" style={{ color: "var(--br-bronze-soft)" }}>+ {formatCurrencyEUR(totals.accrued_net)}</Td>
                    <Td align="right" className="font-mono-fin">{formatCurrencyEUR(totals.daily_net)}</Td>
                    <Td align="right" className="font-mono-fin" style={{ color: "var(--br-bronze)" }}>{formatCurrencyEUR(totals.monthly_est_net)}</Td>
                    <Td align="right" className="font-mono-fin text-[color:var(--br-text-dim)]">{totals.deposits}</Td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {hasMore && (
            <div className="mt-4 text-center">
              <button className="br-btn-outline" onClick={loadMore} disabled={loadingMore} data-testid="aum-load-more">
                {loadingMore ? "Caricamento…" : `Carica altri (${aum.count - rows.length})`}
              </button>
            </div>
          )}
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6 br-fade-up br-delay-2">
          <ActionCard
            title="Genera invito"
            description="Invia un link nominativo a uso unico, valido 7 giorni."
            to="/admin/invites"
            cta="Vai agli inviti"
            testid="action-invites"
          />
          <ActionCard
            title="Apri nuovo deposito"
            description="Inserisci un nuovo vincolo per un cliente esistente con calcolo lineare giornaliero."
            to="/admin/deposits"
            cta="Gestisci depositi"
            testid="action-deposits"
          />
          <ActionCard
            title="Pubblica estratti"
            description="Carica un PDF firmato e collegalo al cliente o al singolo deposito."
            to="/admin/statements"
            cta="Vai agli estratti"
            testid="action-statements"
          />
          <ActionCard
            title="Gestione clienti"
            description="Sospendi, riattiva o promuovi ad amministratore."
            to="/admin/clients"
            cta="Apri clienti"
            testid="action-clients"
          />
        </div>

        <div className="mt-14">
          <h2 className="font-serif-display text-[2rem] mb-6">Ultimi <em>clienti</em></h2>
          <div className="br-card overflow-x-auto" data-testid="admin-recent-clients">
            <table className="w-full text-left">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                  <Th>Nome</Th>
                  <Th>Email</Th>
                  <Th>Ruolo</Th>
                  <Th>Stato</Th>
                  <Th>Creato</Th>
                </tr>
              </thead>
              <tbody>
                {users.slice(0, 8).map((u) => (
                  <tr key={u.id} className="border-b" style={{ borderColor: "var(--br-line)" }} data-testid={`admin-user-row-${u.id}`}>
                    <Td className="text-[color:var(--br-parchment)]">{u.name}</Td>
                    <Td className="font-mono-fin text-[0.85rem]">{u.email}</Td>
                    <Td className="font-mono-fin uppercase text-[0.7rem]" style={{ color: u.role === "admin" ? "var(--br-bronze)" : "var(--br-text-dim)" }}>{u.role}</Td>
                    <Td>
                      <span className="font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase border px-2 py-1" style={{ borderColor: u.status === "active" ? "var(--br-bronze)" : "var(--br-line-bright)", color: u.status === "active" ? "var(--br-bronze)" : "var(--br-text-dim)" }}>
                        {u.status}
                      </span>
                    </Td>
                    <Td className="font-mono-fin text-[color:var(--br-text-dim)]">{formatDate(u.created_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </PortalLayout>
  );
}

function KPI({ label, value, accent = false, testid }) {
  return (
    <div className="br-card p-6" data-testid={testid}>
      <div className="br-overline">{label}</div>
      <div className={`font-mono-fin mt-3 text-[1.5rem] ${accent ? "" : "text-[color:var(--br-parchment)]"}`} style={accent ? { color: "var(--br-bronze)" } : undefined}>
        {value}
      </div>
    </div>
  );
}

function ActionCard({ title, description, to, cta, testid }) {
  return (
    <div className="br-card p-7" data-testid={testid}>
      <h3 className="font-serif-display text-[1.5rem]">{title}</h3>
      <p className="text-[0.92rem] text-[color:var(--br-text-dim)] mt-3 leading-relaxed">{description}</p>
      <Link to={to} className="br-btn-outline mt-5 inline-block" style={{ padding: "10px 16px" }}>
        {cta}
      </Link>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className="px-5 py-4 font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase font-medium" style={{ color: "var(--br-text-dim)", textAlign: align }}>{children}</th>;
}

function Td({ children, align = "left", className = "", style, ...rest }) {
  return <td className={`px-5 py-4 text-[0.9rem] ${className}`} style={{ textAlign: align, ...style }} {...rest}>{children}</td>;
}
